import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSessionContext } from '../adapters/AdapterProvider'
import { DEMO_SCHEMA, flattenSchema, type EntityGroup } from './demo-schema'
import { groupFieldsByCategory } from './group-schema-fields'

export type OnPickFieldCallback = (fieldKey: string) => void

export interface CmdKController {
  open: (handler?: OnPickFieldCallback) => void
  close: () => void
  schema: EntityGroup[]
  recent: string[]
  recordRecent: (fieldKey: string) => void
  isOpen: boolean
  onPick?: OnPickFieldCallback | undefined
  /** Register a consumer for Cmd+K — the shortcut only fires while at least
   *  one consumer is mounted. Returns an unregister callback. */
  registerConsumer: () => () => void
}

const CmdKContext = createContext<CmdKController | undefined>(undefined)

const STORAGE_KEY = 'label-designer.cmdk.recent'
const MAX_RECENT = 5

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string')
  } catch {
    // ignore
  }
  return []
}

export function CmdKProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [recent, setRecent] = useState<string[]>(() => [])
  const onPickRef = useRef<OnPickFieldCallback | undefined>(undefined)
  // Count of mounted consumers. The global Cmd+K shortcut only activates when > 0.
  const consumerCountRef = useRef(0)
  // Stable increment/decrement callbacks, not reactive — we don't need a state re-render.
  const registerConsumer = useCallback((): (() => void) => {
    consumerCountRef.current += 1
    return () => {
      consumerCountRef.current -= 1
    }
  }, [])

  useEffect(() => {
    setRecent(readRecent())
  }, [])

  const open = useCallback((handler?: OnPickFieldCallback) => {
    onPickRef.current = handler
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    onPickRef.current = undefined
  }, [])

  const recordRecent = useCallback((fieldKey: string) => {
    setRecent(prev => {
      const next = [fieldKey, ...prev.filter(k => k !== fieldKey)].slice(0, MAX_RECENT)
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (!meta) return
      if (e.key === 'k' || e.key === 'K') {
        // Only activate when at least one consumer (rules route) is mounted.
        if (consumerCountRef.current < 1) return
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Embedded sessions carry their own field catalog via
  // SessionContext.schemaMeta.fields. The demo adapter's getSessionContext()
  // always resolves `fields: []`, so an empty/absent list falls back to the
  // hardcoded DEMO_SCHEMA (see shell/demo-schema.ts).
  const sessionContext = useSessionContext()
  const schema = useMemo<EntityGroup[]>(() => {
    const fields = sessionContext?.schemaMeta.fields
    return fields && fields.length > 0 ? groupFieldsByCategory(fields) : DEMO_SCHEMA
  }, [sessionContext])

  const value = useMemo<CmdKController>(
    () => ({
      open,
      close,
      schema,
      recent,
      recordRecent,
      isOpen,
      onPick: onPickRef.current,
      registerConsumer,
    }),
    [open, close, schema, recent, recordRecent, isOpen, registerConsumer]
  )

  return <CmdKContext.Provider value={value}>{children}</CmdKContext.Provider>
}

export function useCmdK(): CmdKController {
  const ctx = useContext(CmdKContext)
  if (!ctx) throw new Error('useCmdK must be used inside <CmdKProvider>')
  return ctx
}

/** Mount this hook in any route that has CmdK consumers (i.e. the rules route).
 *  While mounted, the global Cmd+K shortcut is active. */
export function useCmdKConsumer(): void {
  const { registerConsumer } = useCmdK()
  useEffect(() => {
    const unregister = registerConsumer()
    return unregister
  }, [registerConsumer])
}

export { flattenSchema }
